import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { stores } from "../context";
import Menu from "./Menu";
import CategoryMenu from "./CategoryMenu";
import { UserDropdown } from "./UserDropdown";
import LanguageSwitcher from "./LanguageSwitcher";
import { BookingModal } from "./BookingModal";
import { MobileDrawer } from "./MobileDrawer";
import { ChevronDown } from "lucide-react";

const Navbar = observer(() => {
  const { user, cart, ui } = stores;
  const navigate = useNavigate();
  const location = useLocation();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const { t } = useTranslation("common");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // En: Show product categories ONLY on the /menu page
  const isProductsMenuPage = location.pathname === "/menu";

  const logout = () => {
    user.logout();
    cart.clearCart();
    navigate("/");
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* En: The main navigation bar with logo, language switcher, booking button, cart, and user profile/login buttons */}
      <nav className="bg-stone-950/90 backdrop-blur-md sticky top-0 z-50 border-b border-amber-500/20 text-stone-100 shadow-xl py-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* En: Logo -> Only on the Home page (/) */}
            <div className="flex items-center">
              <NavLink
                to="/"
                end
                className="flex items-center space-x-3 flex-shrink-0 group"
              >
                <div className="hidden md:flex items-center h-20">
                  <img
                    src="/logo.svg"
                    alt="Restauracja r-ja.pl"
                    className="h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105 filter drop-shadow-[0_2px_8px_rgba(245,158,11,0.2)]"
                    width="160"
                    height="64"
                  />
                </div>
                <div className="flex md:hidden items-center h-12">
                  <img
                    src="/logo.svg"
                    alt="Restauracja r-ja.pl"
                    className="h-11 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                    width="110"
                    height="44"
                  />
                </div>
              </NavLink>
            </div>

            {/* En: Right side elements (Language, Booking, Cart, Profile) */}
            <div className="flex items-center space-x-3 sm:space-x-4 md:space-x-6 flex-shrink-0">
              <div className="hidden md:block">
                <LanguageSwitcher />
              </div>

              <button
                onClick={() => ui.setIsBookingOpen(true)}
                className="hidden md:flex items-center px-5 py-2.5 rounded-xl font-serif text-xs font-semibold uppercase tracking-widest text-amber-100 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 transition-all duration-300 shadow-lg border border-amber-400/30 cursor-pointer"
              >
                <span>{t("nav.book", "Rezerwacja")}</span>
              </button>

              <button
                onClick={() => cart.setIsOpen(true)}
                className="relative p-2.5 rounded-full text-stone-300 hover:text-amber-400 hover:bg-stone-900 transition cursor-pointer"
                aria-label="Otwórz koszyk"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                {cart.totalQuantity > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-amber-600 text-[10px] font-bold text-white shadow-md">
                    {cart.totalQuantity}
                  </span>
                )}
              </button>

              {user.isAuth ? (
                <div className="flex items-center space-x-2 md:space-x-4 min-w-0">
                  {user.user?.role === "ADMIN" && (
                    <button
                      onClick={() => navigate("/admin")}
                      aria-label="Go to Admin Panel"
                      className="p-2 sm:px-3 sm:py-2 rounded-xl text-xs font-semibold text-amber-400 hover:bg-stone-900 border border-amber-500/20 transition cursor-pointer"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="h-5 w-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </button>
                  )}
                  <div className="relative hidden md:flex" ref={dropdownRef}>
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-stone-900/80 hover:bg-stone-900 border border-amber-500/30 text-stone-200 hover:text-amber-400 transition-all cursor-pointer shadow-sm"
                    >
                      <span className="text-sm font-serif">
                        {t("userMenu.greeting", { name: user.user?.firstName })}
                      </span>

                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-200 ${
                          isDropdownOpen
                            ? "rotate-180 text-amber-400"
                            : "text-stone-400"
                        }`}
                      />
                    </button>
                    <UserDropdown
                      isOpen={isDropdownOpen}
                      logout={logout}
                      onClose={() => setIsDropdownOpen(false)}
                    />
                  </div>
                </div>
              ) : (
                <div className="hidden md:flex items-center space-x-2">
                  <button
                    onClick={() => user.openLoginModal()}
                    className="p-2 sm:px-4 sm:py-2 rounded-xl text-xs font-semibold text-stone-300 hover:text-amber-400 hover:bg-stone-900 transition cursor-pointer"
                  >
                    {t("auth.login")}
                  </button>
                  <button
                    onClick={() => navigate("/registration")}
                    className="p-2 sm:px-4 sm:py-2 rounded-xl text-xs font-semibold text-amber-300 border border-amber-500/40 hover:bg-amber-500/10 transition cursor-pointer"
                  >
                    {t("auth.register")}
                  </button>
                </div>
              )}
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                aria-label="Open mobile menu"
                className="md:hidden p-2 text-stone-300 hover:text-amber-400 cursor-pointer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* En: The main navigation bar with the menu and category menu (only on /menu page) */}
      <div className="hidden md:block sticky top-20 z-20 bg-stone-950/80 backdrop-blur-md border-b border-stone-800/60">
        <Menu />
        {isProductsMenuPage && (
          <div className="border-t border-stone-800/60 bg-stone-900/50">
            <CategoryMenu />
          </div>
        )}
      </div>

      <MobileDrawer
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onOpenBooking={() => ui.setIsBookingOpen(true)}
      />
      <BookingModal
        isOpen={ui.isBookingOpen}
        onClose={() => ui.setIsBookingOpen(false)}
      />
    </>
  );
});

export default Navbar;
